import type { IsoDate } from './dates';

/** A single meal. */
export type Meal = 'breakfast' | 'lunch' | 'dinner';
/**
 * Which meals a plan delivers each day: any non-empty combination, written in
 * meal order and joined with "+" (e.g. "breakfast+lunch"). The same type
 * describes the meals a kitchen serves and the meals paused on a day.
 * The legacy value "both" (lunch + dinner) is accepted by `parseSlot`.
 */
export type Slot =
  | 'breakfast'
  | 'lunch'
  | 'dinner'
  | 'breakfast+lunch'
  | 'breakfast+dinner'
  | 'lunch+dinner'
  | 'breakfast+lunch+dinner';
export type Food = 'veg' | 'nonveg' | 'egg';
export type PayMethod = 'upi' | 'cash';

/** A pause on one day: some or all of the plan's meals. */
export interface Pause {
  day: IsoDate;
  slot: Slot;
}

/** The kitchen's calendar: which weekdays it delivers, and its holidays. */
export interface KitchenCalendar {
  /** ISO weekdays, 1 = Monday ... 7 = Sunday. Monday to Saturday: [1,2,3,4,5,6]. */
  deliveryDays: readonly number[];
  holidays: ReadonlySet<IsoDate> | readonly IsoDate[];
  /** Meals the kitchen serves. A meal moved to one it no longer serves is owed at the end instead. Left out = any. */
  meals?: readonly Meal[];
}

/** What the customer bought. */
export interface Terms {
  startDate: IsoDate;
  /** Number of meal days on the plan (26 for a Mon-Sat month). */
  mealDays: number;
  slot: Slot;
  /**
   * The weekdays this plan delivers on (ISO, 1 = Monday ... 7 = Sunday): any combination, say [1, 2, 3] or [7].
   * Left out, the plan follows the kitchen's delivery days. Holidays apply either way.
   */
  days?: readonly number[];
}

export interface Payment {
  amount: number;
  method: PayMethod;
  paidOn: IsoDate;
}

/** Every meal, in serving order. */
export const MEALS: readonly Meal[] = ['breakfast', 'lunch', 'dinner'];

/** Every valid slot, smallest first. */
export const SLOTS: readonly Slot[] = [
  'breakfast', 'lunch', 'dinner',
  'breakfast+lunch', 'breakfast+dinner', 'lunch+dinner',
  'breakfast+lunch+dinner',
];

/** The meals in a slot, in serving order. */
export function mealsOf(slot: Slot): Meal[] {
  const parts = slot.split('+');
  return MEALS.filter((m) => parts.includes(m));
}

/** The slot for a set of meals (any order, duplicates ignored). Throws on an empty set. */
export function slotOf(meals: Iterable<Meal>): Slot {
  const set = new Set(meals);
  const picked = MEALS.filter((m) => set.has(m));
  if (picked.length === 0) throw new Error('Pick at least one meal');
  return picked.join('+') as Slot;
}

/** Read a slot from stored or typed text. Accepts the legacy "both", any order and "+", "," or "&" separators. */
export function parseSlot(text: string): Slot | null {
  const v = text.trim().toLowerCase();
  if (v === 'both') return 'lunch+dinner';
  if (v === 'all') return 'breakfast+lunch+dinner';
  const words = v.split(/[\s+,&/]+|\band\b/).filter(Boolean);
  const meals = new Set<Meal>();
  for (const w of words) {
    const m = MEALS.find((x) => x === w || (w.length >= 1 && x.startsWith(w)));
    if (!m) return null;
    meals.add(m);
  }
  return meals.size ? slotOf(meals) : null;
}

/** True when every meal in `part` is also in `whole`. */
export function slotIncludes(whole: Slot, part: Slot): boolean {
  const w = mealsOf(whole);
  return mealsOf(part).every((m) => w.includes(m));
}

/** Meals in both slots, or null when they share none. */
export function slotIntersect(a: Slot, b: Slot): Slot | null {
  const bm = mealsOf(b);
  const both = mealsOf(a).filter((m) => bm.includes(m));
  return both.length ? slotOf(both) : null;
}

/** Plain words for a set of meals: "lunch", "breakfast and lunch", "breakfast, lunch and dinner". */
export function mealWords(slot: Slot): string {
  const m = mealsOf(slot);
  return m.length <= 2 ? m.join(' and ') : `${m.slice(0, -1).join(', ')} and ${m[m.length - 1]}`;
}

/** Short label for screens and plan names: "Lunch", "Breakfast + lunch", "All 3 meals". */
export function slotLabel(slot: Slot): string {
  const m = mealsOf(slot);
  if (m.length === 3) return 'All 3 meals';
  const text = m.join(' + ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Picks the active meal from the meals served by the kitchen based on the time of day:
 * - Before 10:30 AM: breakfast (if served), else lunch, else dinner
 * - 10:30 AM to 4:00 PM (16:00): lunch (if served), else dinner, else breakfast
 * - 4:00 PM (16:00) to 11:00 PM (23:00): dinner (if served), else breakfast, else lunch
 * - 11:00 PM onwards: next meal cycle (breakfast if served, else lunch, else dinner)
 */
export function mealForTime(served: Meal[], date = new Date(), timeZone?: string): Meal {
  if (served.length === 0) return 'lunch';
  if (served.length === 1) return served[0]!;

  let hours = date.getHours();
  let minutes = date.getMinutes();

  if (timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    }).formatToParts(date);
    for (const p of parts) {
      if (p.type === 'hour') hours = parseInt(p.value, 10);
      if (p.type === 'minute') minutes = parseInt(p.value, 10);
    }
  }

  const time = hours + minutes / 60;

  // Before 10:30 AM: breakfast time
  if (time < 10.5) {
    if (served.includes('breakfast')) return 'breakfast';
    if (served.includes('lunch')) return 'lunch';
    return served[0]!;
  }

  // 10:30 AM to 4:00 PM (16:00): lunch time
  if (time < 16.0) {
    if (served.includes('lunch')) return 'lunch';
    if (served.includes('dinner')) return 'dinner';
    return served[0]!;
  }

  // 4:00 PM (16:00) to 11:00 PM (23:00): dinner time
  if (time < 23.0) {
    if (served.includes('dinner')) return 'dinner';
    if (served.includes('breakfast')) return 'breakfast';
    return served[0]!;
  }

  // 11:00 PM onwards: late night prep for next morning
  if (served.includes('breakfast')) return 'breakfast';
  if (served.includes('lunch')) return 'lunch';
  return served[0]!;
}
